import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BellOff,
  CheckCircle,
  CirclePlay,
  ExternalLink,
  EyeOff,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Tv,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageMeta from '@/components/seo/PageMeta';

const reviewedDateLabel = 'April 21, 2026';

const comparisonOptions = [
  {
    title: 'Highlight apps',
    Icon: CirclePlay,
    goodFor: 'Catching a short version of a game when highlights are enough.',
    limitation: 'They can solve the highlight problem, but they are not always the right first step when you want to decide whether a full completed game is worth your time.',
    skoreFit: 'Use skore.info before opening a video app. Pick the completed game spoiler-free, then watch it on your own broadcaster, DVR, streaming app, or replay platform.',
    examples: 'Examples reviewed: NoSpoilerz and Spoiler-Free Sports.'
  },
  {
    title: 'Recommendation and rating sites',
    Icon: Search,
    goodFor: 'Getting an opinionated verdict, rating, or recommendation before deciding what to watch.',
    limitation: 'Each product has its own scoring model, coverage, language, and reveal flow. Treat those verdicts as one input, not universal truth.',
    skoreFit: 'skore.info keeps the decision simple: completed games, hidden scores by default, and sport-specific filters you can adjust without creating an account.',
    examples: 'Examples reviewed: SportsRec, TheAfterGame, and Should I Watch Sports.'
  },
  {
    title: 'Score blockers and browser extensions',
    Icon: ShieldCheck,
    goodFor: 'Protecting yourself while browsing score-first sites, search pages, headlines, and other places where results appear by default.',
    limitation: 'A blocker can hide the wrong number or miss a spoiler in a thumbnail, headline, or app notification. It also does not tell you which game is worth watching.',
    skoreFit: 'Use blockers as protection around the rest of the web. Use skore.info for the specific decision: which completed game should I choose next?',
    examples: 'Examples reviewed: hideScores and scoreBlock.'
  },
  {
    title: 'Native hide-score settings',
    Icon: Tv,
    goodFor: 'Staying spoiler-safe inside one league app or viewing platform that already has a hide-score mode.',
    limitation: 'Platform docs can include limits, such as article spoilers, match banners, highlights, recaps, or push notifications outside the hide-score setting.',
    skoreFit: 'Keep those settings on when you watch. Use skore.info separately when you need a cross-sport decision step before opening the platform that has the replay.',
    examples: 'Examples reviewed: NBA Help Center, Apple TV app support, and TOD support.'
  },
  {
    title: 'Manual workarounds',
    Icon: BellOff,
    goodFor: 'Low-tech habits like muting notifications, asking a friend, avoiding social feeds, or opening apps with one eye closed.',
    limitation: 'Manual routines break easily. One notification, thumbnail, standings table, or group chat can spoil the result before you choose.',
    skoreFit: 'Open skore.info first, choose a game from the spoiler-safe list, then go to the service where you already watch that sport.',
    examples: 'No external tool required.'
  }
];

const sportLinks = [
  {
    name: 'NBA',
    path: '/sport/nba',
    description: 'Choose completed basketball games without seeing the final score first.',
  },
  {
    name: 'Soccer',
    path: '/sport/soccer',
    description: 'Filter completed matches by goal totals and available competitions.',
  },
  {
    name: 'MLB',
    path: '/sport/mlb',
    description: 'Find completed baseball games while keeping the result hidden.',
  },
  {
    name: 'NHL',
    path: '/sport/nhl',
    description: 'Browse completed hockey games with spoiler-safe filters.',
  },
];

const sourceGroups = [
  {
    label: 'Recommendation and rating examples',
    note: 'Pages that position around ratings, verdicts, tiers, or hot-game discovery without showing scores first.',
    links: [
      { name: 'SportsRec guide', href: 'https://sportsrec.app/guide/spoiler-free-sports' },
      { name: 'TheAfterGame', href: 'https://theaftergame.app/' },
      { name: 'Should I Watch Sports', href: 'https://shouldiwatchsports.com/' },
    ],
  },
  {
    label: 'Highlight app examples',
    note: 'Pages that focus on score-hidden sports highlights and reveal controls.',
    links: [
      { name: 'NoSpoilerz', href: 'https://nospoilerz.com/' },
      { name: 'Spoiler-Free Sports FAQ', href: 'https://spoilerfreesports.com/faqs' },
    ],
  },
  {
    label: 'Score blocker examples',
    note: 'Browser-extension alternatives that hide scores, match results, or spoiler terms across websites.',
    links: [
      { name: 'hideScores', href: 'https://hidescores.net/' },
      { name: 'scoreBlock FAQ', href: 'https://scoreblock.net/faq' },
    ],
  },
  {
    label: 'Native hide-score settings',
    note: 'Official support pages showing that major sports and viewing platforms recognize spoiler avoidance as a real workflow.',
    links: [
      { name: 'NBA Help Center', href: 'https://support.watch.nba.com/hc/en-us/articles/115000587174-Hiding-Scores-Spoilers-Mode' },
      { name: 'Apple TV app support', href: 'https://support.apple.com/guide/tvapp/follow-sports-teams-and-scores-atvb2c74ee59/web' },
      { name: 'TOD support', href: 'https://support.tod.tv/hc/en-us/articles/19060160698780-What-is-Hide-Scores-mode' },
    ],
  },
];

const SpoilerFreeAlternatives: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col bg-gray-950">
      <PageMeta
        title="Spoiler-Free Sports Alternatives | skore.info"
        description="Compare spoiler-free sports options, from highlight apps to score blockers, then use skore.info to choose completed games to watch elsewhere."
        canonicalPath="/spoiler-free-sports-alternatives"
        keywords={[
          'spoiler-free sports alternatives',
          'hide sports scores',
          'watch sports without spoilers',
          'sports replay chooser',
          'skore.info'
        ]}
      />
      <Header />

      <main className="flex-grow">
        <section className="border-b border-gray-800 bg-gray-950">
          <div className="container mx-auto grid gap-10 px-4 py-12 md:grid-cols-[1.1fr_0.9fr] md:items-center md:py-16">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-indigo-300">
                Spoiler-free sports alternatives
              </p>
              <h1 className="max-w-3xl text-4xl font-bold leading-tight text-white md:text-6xl">
                Choose completed games before the score finds you
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-300">
                Most sports apps solve a different problem: they tell you what happened. skore.info is for the moment before that, when you missed live action and want to choose a completed game without seeing the score, winner, headline, or thumbnail giveaway.
              </p>
              <p className="mt-4 max-w-2xl text-base leading-7 text-gray-400">
                skore.info does not host, stream, sell, record, or provide the replay. Use it to choose the game, then watch on your own broadcaster, streaming app, DVR, or replay platform.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/sport/nba"
                  className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-5 py-3 font-semibold text-white transition-colors hover:bg-indigo-500"
                >
                  Browse NBA games
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <a
                  href="#compare-options"
                  className="inline-flex items-center justify-center rounded-lg border border-gray-700 px-5 py-3 font-semibold text-gray-100 transition-colors hover:border-gray-500 hover:bg-gray-900"
                >
                  Compare options
                </a>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-800 bg-gray-900">
              <div className="border-b border-gray-800 bg-gradient-to-br from-indigo-500/20 via-indigo-500/10 to-cyan-400/10 p-6 md:p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-indigo-200">
                  Spoiler-safe game picker
                </p>
                <h2 className="mt-3 max-w-xl text-2xl font-semibold leading-tight text-white md:text-3xl">
                  Hide scores. Compare completed NBA, soccer, MLB, and NHL games before you watch.
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-7 text-gray-200/90 md:text-base">
                  skore.info helps you choose a completed game without seeing the winner, final score,
                  headline, or thumbnail spoiler first. Then you watch on your own broadcaster,
                  streaming app, DVR, or replay platform.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {['Completed games only', 'Scores hidden by default', 'Cross-sport filters', 'Watch elsewhere'].map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-indigo-100"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="space-y-4 p-6">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-400/10 text-emerald-300">
                    <EyeOff className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-white">Scores hidden by default</h2>
                    <p className="mt-1 text-sm leading-6 text-gray-400">
                      Pick by spoiler-safe signals first. Reveal results only when you are ready.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cyan-400/10 text-cyan-300">
                    <SlidersHorizontal className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-white">A decision step, not a video service</h2>
                    <p className="mt-1 text-sm leading-6 text-gray-400">
                      skore.info helps you choose. Your broadcaster, streaming app, DVR, or replay platform is where you watch.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="compare-options" className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-indigo-300">What to compare</p>
            <h2 className="text-3xl font-bold text-white md:text-4xl">Pick the tool that matches the job</h2>
            <p className="mt-4 text-lg leading-8 text-gray-300">
              Spoiler-free sports is not one feature. Sometimes you need highlights. Sometimes you need a browser shield. Sometimes you need a league app setting. skore.info covers the selection job: decide which completed game is worth your time without seeing the result.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {comparisonOptions.map(({ title, Icon, goodFor, limitation, skoreFit, examples }) => (
              <article key={title} className="rounded-lg border border-gray-800 bg-gray-900/70 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-indigo-300/20 bg-indigo-400/10 text-indigo-200">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{title}</h3>
                    <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Good for</p>
                    <p className="mt-1 leading-7 text-gray-300">{goodFor}</p>
                    <p className="mt-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Plan around</p>
                    <p className="mt-1 leading-7 text-gray-300">{limitation}</p>
                    <p className="mt-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Where skore.info fits</p>
                    <p className="mt-1 leading-7 text-gray-300">{skoreFit}</p>
                    <p className="mt-4 text-sm text-gray-500">{examples}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="border-y border-gray-800 bg-gray-900/45">
          <div className="container mx-auto grid gap-10 px-4 py-14 md:grid-cols-[0.95fr_1.05fr] md:py-20">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-indigo-300">Safe order of operations</p>
              <h2 className="text-3xl font-bold text-white md:text-4xl">Use skore.info before score-first apps</h2>
              <p className="mt-4 text-lg leading-8 text-gray-300">
                The safest workflow is simple: make the viewing decision before you open the places that are designed to tell you the result.
              </p>
              <div className="mt-6">
                <Link
                  to="/blog/2025/03/02/behind-the-defaults"
                  reloadDocument
                  className="inline-flex items-center text-sm font-semibold text-indigo-300 hover:text-indigo-200"
                >
                  Read how the spoiler-free filters work
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>

            <ol className="space-y-4">
              {[
                'Open skore.info first, before score-first apps, news sites, social feeds, or group chats.',
                'Choose a completed game using spoiler-safe filters and signals, not the final score.',
                'Go to the broadcaster, streaming app, DVR, replay platform, or subscription service where you already watch that sport.',
                'Reveal the score only after you are done watching, or when you decide you no longer care about the spoiler.'
              ].map((step, index) => (
                <li key={step} className="flex gap-4 rounded-lg border border-gray-800 bg-gray-950/80 p-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                    {index + 1}
                  </span>
                  <p className="leading-7 text-gray-300">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="container mx-auto px-4 py-14 md:py-20">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-indigo-300">Start spoiler-safe</p>
              <h2 className="text-3xl font-bold text-white md:text-4xl">Choose a sport page</h2>
              <p className="mt-4 max-w-2xl text-lg leading-8 text-gray-300">
                These pages help you browse completed games without seeing scores first. If a sport has no games in the current feed, the sport page will say so.
              </p>
            </div>
            <Link
              to="/faq"
              className="inline-flex items-center text-sm font-semibold text-indigo-300 hover:text-indigo-200"
            >
              Read the FAQ
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {sportLinks.map((sport) => (
              <Link
                key={sport.path}
                to={sport.path}
                className="group rounded-lg border border-gray-800 bg-gray-900/70 p-6 transition-colors hover:border-indigo-400/60 hover:bg-gray-900"
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-2xl font-bold text-white">{sport.name}</h3>
                  <ArrowRight className="h-5 w-5 text-gray-500 transition-transform group-hover:translate-x-1 group-hover:text-indigo-300" />
                </div>
                <p className="mt-4 leading-7 text-gray-300">{sport.description}</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="border-t border-gray-800 bg-gray-900/45">
          <div className="container mx-auto px-4 py-14 md:py-20">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-indigo-300">Source notes</p>
              <h2 className="text-3xl font-bold text-white md:text-4xl">Public pages reviewed</h2>
              <p className="mt-4 text-lg leading-8 text-gray-300">
                External pages were reviewed on {reviewedDateLabel}. They are linked for factual context, not endorsement. Product pages and support docs can change, so use the links when a detail matters.
              </p>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-2">
              {sourceGroups.map((group) => (
                <article key={group.label} className="rounded-lg border border-gray-800 bg-gray-950/80 p-6">
                  <h3 className="text-xl font-semibold text-white">{group.label}</h3>
                  <p className="mt-3 leading-7 text-gray-300">{group.note}</p>
                  <ul className="mt-5 space-y-3">
                    {group.links.map((link) => (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-indigo-300 hover:text-indigo-200"
                        >
                          {link.name}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>

            <div className="mt-10 rounded-lg border border-emerald-300/20 bg-emerald-400/10 p-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="mt-1 h-5 w-5 shrink-0 text-emerald-300" />
                <p className="leading-7 text-emerald-50">
                  Comparison rule: do not ask which tool wins everywhere. Ask which step of the spoiler-free workflow you are trying to protect.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SpoilerFreeAlternatives;
